import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import {
  Camera,
  Building2,
  CheckCircle2,
  Eye,
  EyeOff,
  Filter,
  ImagePlus,
  MessageCircle,
  Minus,
  PackageX,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Send,
  Trash2,
  Truck,
  UserPlus,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import {
  Button,
  EmptyState,
  ErrorState,
  MediaImage,
  Modal,
  Notice,
  Skeleton,
  StatusBadge,
} from "../components/ui";
import { useSession } from "../context/SessionContext";
import { apiRequest, jsonBody } from "../lib/api";
import { formatDateTime, formatRupiah, statusLabel } from "../lib/format";
import {
  blobToBase64,
  optimizeProductImage,
  type OptimizedImage,
} from "../lib/images";
import {
  normalizeCollection,
  type ApiRecord,
  type SessionUser,
} from "../lib/types";
import { BRANCH_TIME_ZONES } from "../../shared/delivery-schedule";
import { OwnerOriginEditor } from "../components/OwnerOriginEditor";

const value = (record: ApiRecord, ...keys: string[]) =>
  keys
    .map((key) => record[key])
    .find((item) => item !== undefined && item !== null);
const asText = (item: unknown, fallback = "") =>
  typeof item === "string" || typeof item === "number"
    ? String(item)
    : fallback;
const asNumber = (item: unknown, fallback = 0) =>
  Number.isFinite(Number(item)) ? Number(item) : fallback;
const asBoolean = (item: unknown, fallback = false) =>
  item === true || item === 1 || item === "1"
    ? true
    : item === false || item === 0 || item === "0"
      ? false
      : fallback;

interface BranchRow {
  id: string;
  code: string;
  name: string;
  address: string;
  whatsappNumber: string;
  phone: string;
  description: string;
  status: string;
  isDefault: boolean;
  archiveExpiresAt: string;
  adminCount: number;
  activeOrderCount: number;
  openingTime: string;
  closingTime: string;
  deliveryEnabled: boolean;
  pickupEnabled: boolean;
  feeType: "FLAT_FEE" | "FREE" | "MINIMUM_ORDER_FREE_DELIVERY";
  flatFee: number;
  freeDeliveryMinimum: number | null;
  acceptOrdersOutsideHours: boolean;
  timeZone: "Asia/Jakarta" | "Asia/Makassar" | "Asia/Jayapura";
  deliveryArea: string;
  deliveryNote: string;
  deliveryWindows: DeliveryWindowRow[];
  shippingV2Enabled: boolean;
  shippingV2ActivationAllowed: boolean;
  shippingV2LocalSandbox: boolean;
  originAddress: string;
  originNormalizedAddress: string;
  originConfirmationStatus: "UNVERIFIED" | "SELECTED" | "CONFIRMED";
  originLatitude: number | null;
  originLongitude: number | null;
  outsideRatePerKm: number | null;
  distanceRounding: "CEIL" | "FLOOR";
  freeRadiusEnabled: boolean;
  freeRadiusKm: number | null;
  freeRadiusScope: "OUTSIDE_DENPASAR_ONLY" | "ALL_AREAS";
  maximumDeliveryDistance: number | null;
  shippingV2ConfigVersion: number;
}

interface DeliveryWindowRow {
  label: string;
  startTime: string;
  endTime: string;
  cutoffTime: string;
}

interface AdminRow {
  id: string;
  name: string;
  username: string;
  branchId: string;
  branchName: string;
  status: string;
  archiveExpiresAt: string;
}

function branchRow(item: ApiRecord): BranchRow {
  const rawWindows = value(
    item,
    "deliveryWindows",
    "delivery_windows_json",
  );
  const windowItems = (() => {
    if (Array.isArray(rawWindows)) return rawWindows as unknown[];
    if (typeof rawWindows !== "string") return [];
    try {
      const parsed: unknown = JSON.parse(rawWindows);
      return Array.isArray(parsed) ? (parsed as unknown[]) : [];
    } catch {
      return [];
    }
  })();
  return {
    id: asText(item.id),
    code: asText(item.code),
    name: asText(item.name),
    address: asText(item.address),
    whatsappNumber: asText(value(item, "whatsappNumber", "whatsapp_number")),
    phone: asText(item.phone),
    description: asText(item.description),
    status: asText(item.status, "active"),
    isDefault: asBoolean(value(item, "isDefault", "is_default")),
    archiveExpiresAt: asText(
      value(item, "archiveExpiresAt", "archive_expires_at"),
    ),
    adminCount: asNumber(value(item, "adminCount", "admin_count")),
    activeOrderCount: asNumber(
      value(item, "activeOrderCount", "active_order_count"),
    ),
    openingTime: asText(value(item, "openingTime", "opening_time"), "07:00"),
    closingTime: asText(value(item, "closingTime", "closing_time"), "21:00"),
    deliveryEnabled: asBoolean(
      value(item, "deliveryEnabled", "delivery_enabled"),
      true,
    ),
    pickupEnabled: asBoolean(
      value(item, "pickupEnabled", "pickup_enabled"),
      true,
    ),
    feeType: asText(
      value(item, "feeType", "fee_type"),
      "FLAT_FEE",
    ) as BranchRow["feeType"],
    flatFee: asNumber(value(item, "flatFee", "flat_fee")),
    freeDeliveryMinimum:
      value(item, "freeDeliveryMinimum", "free_delivery_minimum") === null
        ? null
        : asNumber(
            value(item, "freeDeliveryMinimum", "free_delivery_minimum"),
          ),
    acceptOrdersOutsideHours: asBoolean(
      value(
        item,
        "acceptOrdersOutsideHours",
        "accept_orders_outside_hours",
      ),
      true,
    ),
    timeZone: asText(
      value(item, "timeZone", "iana_timezone"),
      "Asia/Makassar",
    ) as BranchRow["timeZone"],
    deliveryArea: asText(value(item, "deliveryArea", "delivery_area")),
    deliveryNote: asText(value(item, "deliveryNote", "delivery_note")),
    deliveryWindows: windowItems.map((window, index) => {
      const row =
        window && typeof window === "object" && !Array.isArray(window)
          ? (window as ApiRecord)
          : {};
      return {
        label: asText(row.label, `Jadwal ${index + 1}`),
        startTime: asText(value(row, "startTime", "start_time"), "06:00"),
        endTime: asText(value(row, "endTime", "end_time"), "10:00"),
        cutoffTime: asText(
          value(row, "cutoffTime", "cutoff_time"),
          "05:00",
        ),
      };
    }),
    shippingV2Enabled: asBoolean(value(item, "shippingV2Enabled", "shipping_v2_enabled")),
    shippingV2ActivationAllowed: asBoolean(
      value(item, "shippingV2ActivationAllowed", "shipping_v2_activation_allowed"),
    ),
    shippingV2LocalSandbox: asBoolean(
      value(item, "shippingV2LocalSandbox", "shipping_v2_local_sandbox"),
    ),
    originAddress: asText(value(item, "originAddress", "origin_address")),
    originNormalizedAddress: asText(value(item, "originNormalizedAddress", "origin_normalized_address")),
    originConfirmationStatus: asText(value(item, "originConfirmationStatus", "origin_confirmation_status"), "UNVERIFIED") as BranchRow["originConfirmationStatus"],
    originLatitude: value(item, "originLatitude", "origin_latitude") == null ? null : asNumber(value(item, "originLatitude", "origin_latitude")),
    originLongitude: value(item, "originLongitude", "origin_longitude") == null ? null : asNumber(value(item, "originLongitude", "origin_longitude")),
    outsideRatePerKm: value(item, "outsideRatePerKm", "outside_rate_per_km") == null ? null : asNumber(value(item, "outsideRatePerKm", "outside_rate_per_km")),
    distanceRounding: asText(value(item, "distanceRounding", "distance_rounding"), "CEIL") as BranchRow["distanceRounding"],
    freeRadiusEnabled: asBoolean(value(item, "freeRadiusEnabled", "free_radius_enabled")),
    freeRadiusKm: value(item, "freeRadiusKm", "free_radius_km") == null ? null : asNumber(value(item, "freeRadiusKm", "free_radius_km")),
    freeRadiusScope: asText(value(item, "freeRadiusScope", "free_radius_scope"), "OUTSIDE_DENPASAR_ONLY") as BranchRow["freeRadiusScope"],
    maximumDeliveryDistance: value(item, "maximumDeliveryDistance", "maximum_delivery_distance") == null ? null : asNumber(value(item, "maximumDeliveryDistance", "maximum_delivery_distance")),
    shippingV2ConfigVersion: asNumber(value(item, "shippingV2ConfigVersion", "shipping_v2_config_version"), 1),
  };
}

function adminRow(item: ApiRecord): AdminRow {
  return {
    id: asText(item.id),
    name: asText(item.name),
    username: asText(item.username),
    branchId: asText(value(item, "branchId", "branch_id")),
    branchName: asText(
      value(item, "branchName", "branch_name"),
      "Belum ditetapkan",
    ),
    status: asText(item.status, "active"),
    archiveExpiresAt: asText(
      value(item, "archiveExpiresAt", "archive_expires_at"),
    ),
  };
}

export function BranchAccessPage() {
  const { pathname } = useLocation();
  const adminMode = pathname.endsWith("/admin");
  const [branches, setBranches] = useState<BranchRow[]>([]);
  const [archivedBranches, setArchivedBranches] = useState<BranchRow[]>([]);
  const [admins, setAdmins] = useState<AdminRow[]>([]);
  const [archivedAdmins, setArchivedAdmins] = useState<AdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [branchEdit, setBranchEdit] = useState<BranchRow>();
  const [newBranch, setNewBranch] = useState(false);
  const [adminEdit, setAdminEdit] = useState<AdminRow>();
  const [newAdmin, setNewAdmin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [branchArchiveTarget, setBranchArchiveTarget] = useState<BranchRow>();
  const [adminArchiveTarget, setAdminArchiveTarget] = useState<AdminRow>();

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      if (adminMode) {
        const [branchData, adminData, archiveData] = await Promise.all([
          apiRequest<unknown>("/api/owner/branches"),
          apiRequest<unknown>("/api/owner/admins"),
          apiRequest<unknown>("/api/owner/admins?view=archived"),
        ]);
        setBranches(normalizeCollection(branchData).items.map(branchRow));
        setAdmins(normalizeCollection(adminData).items.map(adminRow));
        setArchivedAdmins(
          normalizeCollection(archiveData).items.map(adminRow),
        );
      } else {
        const [branchData, archiveData] = await Promise.all([
          apiRequest<unknown>("/api/owner/branches"),
          apiRequest<unknown>("/api/owner/branches?view=archived"),
        ]);
        setBranches(normalizeCollection(branchData).items.map(branchRow));
        setArchivedBranches(
          normalizeCollection(archiveData).items.map(branchRow),
        );
      }
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Akses cabang belum dapat dimuat.",
      );
    } finally {
      setLoading(false);
    }
  }, [adminMode]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);

  const saveBranch = async (event: FormEvent) => {
    event.preventDefault();
    if (!branchEdit) return;
    setBusy(true);
    setNotice(undefined);
    try {
      const payload = {
            code: branchEdit.code,
            name: branchEdit.name,
            address: branchEdit.address,
            whatsappNumber: branchEdit.whatsappNumber,
            phone: branchEdit.phone || null,
            openingTime: branchEdit.openingTime,
            closingTime: branchEdit.closingTime,
            description: branchEdit.description || null,
            status: branchEdit.status,
            isDefault: branchEdit.isDefault,
            deliveryEnabled: branchEdit.deliveryEnabled,
            pickupEnabled: branchEdit.pickupEnabled,
            feeType: branchEdit.feeType,
            flatFee: branchEdit.flatFee,
            freeDeliveryMinimum: branchEdit.freeDeliveryMinimum,
            acceptOrdersOutsideHours: branchEdit.acceptOrdersOutsideHours,
            timeZone: branchEdit.timeZone,
            deliveryArea: branchEdit.deliveryArea || null,
            deliveryWindows: branchEdit.deliveryWindows,
            deliveryNote: branchEdit.deliveryNote || null,
            shippingV2Enabled: branchEdit.shippingV2Enabled,
            originLatitude: branchEdit.originLatitude,
            originLongitude: branchEdit.originLongitude,
            outsideRatePerKm: branchEdit.outsideRatePerKm,
            distanceRounding: branchEdit.distanceRounding,
            freeRadiusEnabled: branchEdit.freeRadiusEnabled,
            freeRadiusKm: branchEdit.freeRadiusKm,
            freeRadiusScope: branchEdit.freeRadiusScope,
            maximumDeliveryDistance: branchEdit.maximumDeliveryDistance,
            shippingV2ConfigVersion: branchEdit.shippingV2ConfigVersion,
          };
      if (newBranch) {
        await apiRequest("/api/owner/branches", {
          method: "POST",
          ...jsonBody(payload),
        });
      } else {
        await apiRequest(
          `/api/owner/branches/${encodeURIComponent(branchEdit.id)}`,
          { method: "PATCH", ...jsonBody(payload) },
        );
      }
      setBranchEdit(undefined);
      setNewBranch(false);
      setNotice(
        newBranch
          ? "Cabang berhasil dibuat. Jika statusnya aktif, cabang langsung tersedia di website utama."
          : "Data cabang dan pengiriman berhasil diperbarui di seluruh tampilan aktif.",
      );
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Cabang belum berhasil disimpan.",
      );
    } finally {
      setBusy(false);
    }
  };

  const saveAdmin = async (event: FormEvent) => {
    event.preventDefault();
    if (!adminEdit) return;
    setBusy(true);
    setNotice(undefined);
    try {
      if (newAdmin) {
        await apiRequest("/api/owner/admins", {
          method: "POST",
          ...jsonBody({
            name: adminEdit.name,
            username: adminEdit.username,
            branchId: adminEdit.branchId,
            password,
          }),
        });
      } else {
        await apiRequest(
          `/api/owner/admins/${encodeURIComponent(adminEdit.id)}`,
          {
            method: "PATCH",
            ...jsonBody({
              name: adminEdit.name,
              username: adminEdit.username,
              branchId: adminEdit.branchId,
              status: adminEdit.status,
            }),
          },
        );
        if (password) {
          await apiRequest(
            `/api/owner/admins/${encodeURIComponent(adminEdit.id)}/reset-password`,
            { method: "POST", ...jsonBody({ password }) },
          );
        }
      }
      if (password) setTemporaryPassword(password);
      setAdminEdit(undefined);
      setNewAdmin(false);
      setPassword("");
      setShowPassword(false);
      setNotice(
        newAdmin
          ? "Admin berhasil dibuat."
          : "Data akses Admin berhasil diperbarui.",
      );
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Akses Admin belum berhasil disimpan.",
      );
    } finally {
      setBusy(false);
    }
  };

  const openCreateAdmin = () => {
    setNewAdmin(true);
    setPassword("");
    setAdminEdit({
      id: "",
      name: "",
      username: "",
      branchId: branches[0]?.id ?? "",
      branchName: "",
      status: "active",
      archiveExpiresAt: "",
    });
  };

  const openCreateBranch = () => {
    setNewBranch(true);
    setBranchEdit({
      id: "",
      code: "",
      name: "",
      address: "",
      whatsappNumber: "",
      phone: "",
      description: "",
      status: "active",
      isDefault: false,
      archiveExpiresAt: "",
      adminCount: 0,
      activeOrderCount: 0,
      openingTime: "05:00",
      closingTime: "21:00",
      deliveryEnabled: false,
      pickupEnabled: true,
      feeType: "FLAT_FEE",
      flatFee: 0,
      freeDeliveryMinimum: null,
      acceptOrdersOutsideHours: true,
      timeZone: "Asia/Makassar",
      deliveryArea: "",
      deliveryNote: "",
      deliveryWindows: [],
      shippingV2Enabled: false,
      shippingV2ActivationAllowed: false,
      shippingV2LocalSandbox: false,
      originAddress: "",
      originNormalizedAddress: "",
      originConfirmationStatus: "UNVERIFIED",
      originLatitude: null,
      originLongitude: null,
      outsideRatePerKm: null,
      distanceRounding: "CEIL",
      freeRadiusEnabled: false,
      freeRadiusKm: null,
      freeRadiusScope: "OUTSIDE_DENPASAR_ONLY",
      maximumDeliveryDistance: null,
      shippingV2ConfigVersion: 1,
    });
  };

  const archiveBranch = async () => {
    if (!branchArchiveTarget) return;
    setBusy(true);
    try {
      await apiRequest(
        `/api/owner/branches/${encodeURIComponent(branchArchiveTarget.id)}/archive`,
        { method: "POST", ...jsonBody({}) },
      );
      setBranchArchiveTarget(undefined);
      setNotice("Cabang dipindahkan ke arsip selama 30 hari.");
      await load();
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : "Cabang belum berhasil diarsipkan.");
    } finally {
      setBusy(false);
    }
  };

  const archiveAdmin = async () => {
    if (!adminArchiveTarget) return;
    setBusy(true);
    try {
      await apiRequest(
        `/api/owner/admins/${encodeURIComponent(adminArchiveTarget.id)}/archive`,
        { method: "POST", ...jsonBody({}) },
      );
      setAdminArchiveTarget(undefined);
      setNotice("Admin dipindahkan ke arsip selama 30 hari dan semua sesinya diputus.");
      await load();
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : "Admin belum berhasil diarsipkan.");
    } finally {
      setBusy(false);
    }
  };

  const restoreAccess = async (kind: "branches" | "admins", id: string) => {
    setBusy(true);
    try {
      await apiRequest(
        `/api/owner/${kind}/${encodeURIComponent(id)}/restore`,
        { method: "POST", ...jsonBody({}) },
      );
      setNotice(`${kind === "branches" ? "Cabang" : "Admin"} berhasil dipulihkan.`);
      await load();
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : "Data belum berhasil dipulihkan.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-heading">
        <div>
          <span className="eyebrow">
            {adminMode ? "Owner · akun cabang" : "Owner · identitas toko"}
          </span>
          <h1>{adminMode ? "Admin & Akses" : "Cabang"}</h1>
          <p>
            {adminMode
              ? "Buat akun Admin secara lengkap, pilih tepat satu cabang, dan kelola aksesnya."
              : "Buka, tambah, dan kelola identitas toko yang tampil pada website utama."}
          </p>
        </div>
        <div className="heading-actions">
          <Button
            variant="secondary"
            onClick={() => void load()}
            icon={<RefreshCw />}
          >
            Perbarui
          </Button>
          {adminMode ? (
            <Button onClick={openCreateAdmin} icon={<UserPlus />}>
              Tambah Admin
            </Button>
          ) : (
            <Button onClick={openCreateBranch} icon={<Building2 />}>
              Tambah Cabang
            </Button>
          )}
        </div>
      </div>
      {adminMode ? (
        <Notice tone="info">
          Password lama tidak dapat ditampilkan karena tersimpan sebagai hash
          keamanan. Owner dapat membuat/reset password baru, melihatnya sebelum
          disimpan, lalu sesi Admin lama otomatis diputus.
        </Notice>
      ) : (
        <Notice tone="info">
          Cabang berstatus Aktif otomatis tersedia di website utama. Hapus
          memindahkan cabang ke arsip 30 hari dan tidak menghapus histori order
          atau stok secara paksa.
        </Notice>
      )}
      {notice && (
        <Notice tone={notice.includes("berhasil") ? "success" : "danger"}>
          {notice}
        </Notice>
      )}
      {loading && <Skeleton rows={6} cards />}
      {error && <ErrorState message={error} onRetry={() => void load()} />}
      {!loading && !error && (
        <>
          {!adminMode && <section className="operations-section">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">Identitas toko</span>
                <h2>Cabang</h2>
                <p>
                  Perubahan nama langsung dipakai katalog dan pesanan aktif;
                  histori pesanan selesai tetap dipertahankan.
                </p>
              </div>
            </div>
            {branches.length === 0 ? (
              <EmptyState title="Belum ada cabang" />
            ) : <div className="access-card-grid">
              {branches.map((branch) => (
                <article className="access-card" key={branch.id}>
                  <div className="access-card__header">
                    <span className="branch-code">{branch.code}</span>
                    <StatusBadge status={branch.status} />
                  </div>
                  <h3>{branch.name}</h3>
                  {branch.isDefault && <span className="access-card__default">Cabang default website</span>}
                  <p>{branch.address}</p>
                  <dl>
                    <div>
                      <dt>WhatsApp chat & notifikasi</dt>
                      <dd>{branch.whatsappNumber || "Belum diisi"}</dd>
                    </div>
                    <div>
                      <dt>Jam layanan</dt>
                      <dd>
                        {branch.openingTime}–{branch.closingTime}
                      </dd>
                    </div>
                    <div>
                      <dt>Pengiriman</dt>
                      <dd>
                        {branch.deliveryEnabled ? "Aktif" : "Nonaktif"}
                      </dd>
                    </div>
                    <div>
                      <dt>Admin / pesanan aktif</dt>
                      <dd>{branch.adminCount} Admin · {branch.activeOrderCount} pesanan</dd>
                    </div>
                  </dl>
                  <div className="access-card__actions">
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setNewBranch(false);
                        setBranchEdit(branch);
                      }}
                      icon={<Pencil />}
                    >
                      Buka & Ubah
                    </Button>
                    <Button
                      variant="danger"
                      disabled={branch.isDefault || branch.activeOrderCount > 0}
                      onClick={() => setBranchArchiveTarget(branch)}
                      icon={<Trash2 />}
                    >
                      Hapus
                    </Button>
                  </div>
                  {(branch.isDefault || branch.activeOrderCount > 0) && (
                    <small className="access-card__guard">
                      {branch.isDefault
                        ? "Pindahkan cabang default sebelum menghapus."
                        : "Selesaikan pesanan aktif sebelum menghapus."}
                    </small>
                  )}
                </article>
              ))}
            </div>}
          </section>}
          {adminMode && <section className="operations-section">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">Akun cabang</span>
                <h2>Admin</h2>
              </div>
            </div>
            {admins.length === 0 ? (
              <EmptyState title="Belum ada Admin" />
            ) : (
              <div className="access-card-grid">
                {admins.map((admin) => (
                  <article className="access-card" key={admin.id}>
                    <div className="access-card__header">
                      <strong>@{admin.username}</strong>
                      <StatusBadge status={admin.status} />
                    </div>
                    <h3>{admin.name}</h3>
                    <p>Admin Cabang · {admin.branchName} 🔒</p>
                    <div className="access-card__actions">
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setNewAdmin(false);
                          setPassword("");
                          setAdminEdit(admin);
                        }}
                        icon={<Pencil />}
                      >
                        Buka & Ubah
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => setAdminArchiveTarget(admin)}
                        icon={<Trash2 />}
                      >
                        Hapus
                      </Button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>}
          {!adminMode && archivedBranches.length > 0 && (
            <section className="operations-section access-archive-section">
              <div className="panel-heading"><div><span className="eyebrow">Pemulihan 30 hari</span><h2>Arsip Cabang</h2><p>Setelah batas waktu, cabang tidak dapat dipulihkan lagi.</p></div></div>
              <div className="access-archive-list">
                {archivedBranches.map((branch) => (
                  <article key={branch.id}>
                    <div><strong>{branch.name}</strong><small>{branch.code} · pulihkan sebelum {formatDateTime(branch.archiveExpiresAt)}</small></div>
                    <Button variant="secondary" busy={busy} onClick={() => void restoreAccess("branches", branch.id)} icon={<RotateCcw />}>Pulihkan</Button>
                  </article>
                ))}
              </div>
            </section>
          )}
          {adminMode && archivedAdmins.length > 0 && (
            <section className="operations-section access-archive-section">
              <div className="panel-heading"><div><span className="eyebrow">Pemulihan 30 hari</span><h2>Arsip Admin</h2><p>Akun tidak dapat login selama berada di arsip.</p></div></div>
              <div className="access-archive-list">
                {archivedAdmins.map((admin) => (
                  <article key={admin.id}>
                    <div><strong>{admin.name}</strong><small>@{admin.username} · {admin.branchName} · pulihkan sebelum {formatDateTime(admin.archiveExpiresAt)}</small></div>
                    <Button variant="secondary" busy={busy} onClick={() => void restoreAccess("admins", admin.id)} icon={<RotateCcw />}>Pulihkan</Button>
                  </article>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      <Modal
        open={Boolean(branchEdit)}
        onClose={() => {
          setBranchEdit(undefined);
          setNewBranch(false);
        }}
        title={newBranch ? "Tambah Cabang" : "Buka & Ubah Cabang"}
        description="Identitas toko aktif otomatis dipakai di main website dan dashboard."
      >
        {branchEdit && (
          <form
            className="modal-form"
            onSubmit={(event) => void saveBranch(event)}
          >
            <div className="access-form-heading">
              <strong>Identitas Toko Cabang</strong>
              <small>Isi data yang dilihat pelanggan dan tim operasional.</small>
            </div>
            <label>
              Kode cabang
              <input
                required
                minLength={2}
                maxLength={8}
                pattern="[A-Za-z0-9]+"
                placeholder="Contoh: SDM"
                value={branchEdit.code}
                onChange={(event) =>
                  setBranchEdit({
                    ...branchEdit,
                    code: event.target.value.toUpperCase(),
                  })
                }
              />
              <span>2–8 huruf/angka unik untuk identitas internal.</span>
            </label>
            <label>
              Nama cabang
              <input
                required
                minLength={3}
                value={branchEdit.name}
                onChange={(event) =>
                  setBranchEdit({ ...branchEdit, name: event.target.value })
                }
              />
            </label>
            <label>
              Nomor WhatsApp
              <input
                required
                inputMode="tel"
                placeholder="Kode negara + nomor WhatsApp Admin"
                value={branchEdit.whatsappNumber}
                onChange={(event) =>
                  setBranchEdit({
                    ...branchEdit,
                    whatsappNumber: event.target.value,
                  })
                }
              />
              <span>
                Bisa negara mana saja. Gunakan kode negara seperti +61 atau +62;
                sistem tidak menambahkan 62 otomatis.
              </span>
            </label>
            <label>
              Nomor telepon toko (opsional)
              <input
                inputMode="tel"
                value={branchEdit.phone}
                onChange={(event) =>
                  setBranchEdit({ ...branchEdit, phone: event.target.value })
                }
              />
            </label>
            <label>
              Alamat
              <textarea
                required
                rows={3}
                value={branchEdit.address}
                onChange={(event) =>
                  setBranchEdit({ ...branchEdit, address: event.target.value })
                }
              />
            </label>
            <label>
              Keterangan cabang (opsional)
              <textarea
                rows={3}
                maxLength={500}
                placeholder="Informasi singkat cabang untuk pelanggan"
                value={branchEdit.description}
                onChange={(event) =>
                  setBranchEdit({
                    ...branchEdit,
                    description: event.target.value,
                  })
                }
              />
            </label>
            <div className="two-field-grid">
              <label>
                Jam buka
                <input
                  required
                  type="time"
                  value={branchEdit.openingTime}
                  onChange={(event) =>
                    setBranchEdit({
                      ...branchEdit,
                      openingTime: event.target.value,
                    })
                  }
                />
              </label>
              <label>
                Jam tutup
                <input
                  required
                  type="time"
                  value={branchEdit.closingTime}
                  onChange={(event) =>
                    setBranchEdit({
                      ...branchEdit,
                      closingTime: event.target.value,
                    })
                  }
                />
              </label>
            </div>
            <label>
              Status
              <select
                value={branchEdit.status}
                onChange={(event) =>
                  setBranchEdit({ ...branchEdit, status: event.target.value })
                }
              >
                <option value="active">Aktif</option>
                <option value="temporarily_closed">Tutup sementara</option>
                <option value="inactive">Nonaktif</option>
              </select>
              <span>
                Hanya cabang Aktif atau Tutup sementara yang muncul di website.
              </span>
            </label>
            <label className="toggle-setting">
              <input
                type="checkbox"
                checked={branchEdit.isDefault}
                onChange={(event) =>
                  setBranchEdit({
                    ...branchEdit,
                    isDefault: event.target.checked,
                  })
                }
              />
              <span>
                <strong>Jadikan cabang default website</strong>
                <small>
                  Pelanggan baru pertama kali akan membuka katalog cabang ini.
                </small>
              </span>
            </label>
            <fieldset className="delivery-settings-editor">
              <legend>
                <Truck aria-hidden="true" /> Pengiriman & pengambilan
              </legend>
              <label className="toggle-setting">
                <input
                  type="checkbox"
                  checked={branchEdit.deliveryEnabled}
                  onChange={(event) =>
                    setBranchEdit({
                      ...branchEdit,
                      deliveryEnabled: event.target.checked,
                    })
                  }
                />
                <span>
                  <strong>Aktifkan pengiriman dari cabang ini</strong>
                  <small>
                    Jika nonaktif, pelanggan tetap dapat melihat cabang tetapi
                    pilihan Antar tidak muncul saat checkout.
                  </small>
                </span>
              </label>
              <label className="toggle-setting">
                <input
                  type="checkbox"
                  checked={branchEdit.pickupEnabled}
                  onChange={(event) =>
                    setBranchEdit({
                      ...branchEdit,
                      pickupEnabled: event.target.checked,
                    })
                  }
                />
                <span>
                  <strong>Aktifkan ambil di toko</strong>
                  <small>Pelanggan dapat mengambil pesanan di cabang.</small>
                </span>
              </label>
              {branchEdit.deliveryEnabled && (
                <div className="delivery-settings-editor__details">
                  <label>
                    Area pengiriman
                    <input
                      value={branchEdit.deliveryArea}
                      placeholder="Contoh: Area Denpasar"
                      onChange={(event) =>
                        setBranchEdit({
                          ...branchEdit,
                          deliveryArea: event.target.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    Aturan ongkir
                    <select
                      value={branchEdit.feeType}
                      onChange={(event) =>
                        setBranchEdit({
                          ...branchEdit,
                          feeType: event.target.value as BranchRow["feeType"],
                        })
                      }
                    >
                      <option value="FLAT_FEE">Ongkir tetap</option>
                      <option value="FREE">Selalu gratis</option>
                      <option value="MINIMUM_ORDER_FREE_DELIVERY">
                        Gratis setelah minimum belanja
                      </option>
                    </select>
                  </label>
                  {branchEdit.feeType !== "FREE" && (
                    <label>
                      Ongkir di bawah minimum (Rp)
                      <input
                        type="number"
                        min="0"
                        step="1000"
                        value={branchEdit.flatFee}
                        onChange={(event) =>
                          setBranchEdit({
                            ...branchEdit,
                            flatFee: asNumber(event.target.value),
                          })
                        }
                      />
                    </label>
                  )}
                  {branchEdit.feeType ===
                    "MINIMUM_ORDER_FREE_DELIVERY" && (
                    <label>
                      Minimum belanja gratis ongkir (Rp)
                      <input
                        type="number"
                        min="0"
                        step="1000"
                        value={branchEdit.freeDeliveryMinimum ?? ""}
                        onChange={(event) =>
                          setBranchEdit({
                            ...branchEdit,
                            freeDeliveryMinimum: event.target.value
                              ? asNumber(event.target.value)
                              : null,
                          })
                        }
                      />
                    </label>
                  )}
                  <label>
                    Zona waktu cabang
                    <select
                      value={branchEdit.timeZone}
                      onChange={(event) =>
                        setBranchEdit({
                          ...branchEdit,
                          timeZone: event.target.value as BranchRow["timeZone"],
                        })
                      }
                    >
                      {BRANCH_TIME_ZONES.map((zone) => (
                        <option value={zone.value} key={zone.value}>
                          {zone.label}
                        </option>
                      ))}
                    </select>
                    <span>
                      Dipakai untuk jam buka, waktu pengiriman, dan last order.
                    </span>
                  </label>
                  <label className="toggle-setting">
                    <input
                      type="checkbox"
                      checked={branchEdit.acceptOrdersOutsideHours}
                      onChange={(event) =>
                        setBranchEdit({
                          ...branchEdit,
                          acceptOrdersOutsideHours: event.target.checked,
                        })
                      }
                    />
                    <span>
                      <strong>Terima pesanan setelah last order</strong>
                      <small>
                        Jika aktif, pesanan setelah batas waktu diterima untuk
                        diproses besok. Jika tidak aktif, checkout akan ditolak
                        setelah semua last order hari ini lewat.
                      </small>
                    </span>
                  </label>
                  <div className="delivery-window-editor">
                    <div className="delivery-window-editor__heading">
                      <div>
                        <strong>
                          Waktu pengiriman (
                          {BRANCH_TIME_ZONES.find(
                            (zone) => zone.value === branchEdit.timeZone,
                          )?.shortLabel ?? "WITA"}
                          )
                        </strong>
                        <small>
                          Isi jam antar dan batas terakhir pemesanan.
                        </small>
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() =>
                          setBranchEdit({
                            ...branchEdit,
                            deliveryWindows: [
                              ...branchEdit.deliveryWindows,
                              {
                                label: `Jadwal ${branchEdit.deliveryWindows.length + 1}`,
                                startTime: "06:00",
                                endTime: "10:00",
                                cutoffTime: "05:00",
                              },
                            ],
                          })
                        }
                        icon={<Plus />}
                      >
                        Tambah Jadwal
                      </Button>
                    </div>
                    {branchEdit.deliveryWindows.length === 0 && (
                      <Notice tone="warning">
                        Belum ada jadwal. Tambahkan jadwal sebelum pengiriman
                        digunakan secara operasional.
                      </Notice>
                    )}
                    {branchEdit.deliveryWindows.map((window, index) => (
                      <div className="delivery-window-row" key={index}>
                        <label>
                          Nama
                          <input
                            required
                            value={window.label}
                            onChange={(event) => {
                              const deliveryWindows = [
                                ...branchEdit.deliveryWindows,
                              ];
                              deliveryWindows[index] = {
                                ...window,
                                label: event.target.value,
                              };
                              setBranchEdit({
                                ...branchEdit,
                                deliveryWindows,
                              });
                            }}
                          />
                        </label>
                        {(
                          [
                            ["Mulai", "startTime"],
                            ["Selesai", "endTime"],
                            ["Last order", "cutoffTime"],
                          ] as const
                        ).map(([label, field]) => (
                          <label key={field}>
                            {label}
                            <input
                              required
                              type="time"
                              value={window[field]}
                              onChange={(event) => {
                                const deliveryWindows = [
                                  ...branchEdit.deliveryWindows,
                                ];
                                deliveryWindows[index] = {
                                  ...window,
                                  [field]: event.target.value,
                                };
                                setBranchEdit({
                                  ...branchEdit,
                                  deliveryWindows,
                                });
                              }}
                            />
                          </label>
                        ))}
                        <button
                          type="button"
                          className="icon-danger-button"
                          aria-label={`Hapus jadwal ${window.label}`}
                          onClick={() =>
                            setBranchEdit({
                              ...branchEdit,
                              deliveryWindows:
                                branchEdit.deliveryWindows.filter(
                                  (_, itemIndex) => itemIndex !== index,
                                ),
                            })
                          }
                        >
                          <Trash2 aria-hidden="true" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <label>
                    Catatan pengiriman
                    <textarea
                      rows={3}
                      value={branchEdit.deliveryNote}
                      onChange={(event) =>
                        setBranchEdit({
                          ...branchEdit,
                          deliveryNote: event.target.value,
                        })
                      }
                    />
                  </label>
                </div>
              )}
              <details className="delivery-settings-editor__details">
                <summary>Persiapan Shipping V2</summary>
                {branchEdit.shippingV2LocalSandbox && (
                  <Notice tone="info">
                    LOCAL SHIPPING LAB · Database lokal · Provider MOCK ·
                    panggilan provider nyata tetap 0. Owner mengaktifkan atau
                    mematikan cabang ini secara manual.
                  </Notice>
                )}
                <label>
                  Mode perhitungan ongkir
                  <select
                    value={branchEdit.shippingV2Enabled ? "COMBINED" : "LEGACY"}
                    disabled={!branchEdit.shippingV2Enabled && !branchEdit.shippingV2ActivationAllowed}
                    onChange={(event) =>
                      setBranchEdit({
                        ...branchEdit,
                        shippingV2Enabled: event.target.value === "COMBINED",
                      })
                    }
                  >
                    <option value="LEGACY">Ongkir lama</option>
                    <option value="COMBINED">
                      Shipping V2: radius gratis + tarif kelebihan per km
                    </option>
                  </select>
                  <span>
                    {branchEdit.shippingV2Enabled
                      ? "Jarak dihitung dari origin cabang ke patokan pelanggan. Ongkir dasar tetap mengikuti aturan toko; setelah syarat gratis terpenuhi, hanya jarak di atas radius yang dikenakan tarif."
                      : "Semua tujuan memakai tampilan dan perhitungan ongkir lama cabang ini."}
                  </span>
                  {!branchEdit.shippingV2ActivationAllowed && !branchEdit.shippingV2Enabled && (
                    <span>Dikunci pada Ongkir lama sampai izin aktivasi tersedia.</span>
                  )}
                </label>
                {!newBranch ? <OwnerOriginEditor branchId={branchEdit.id} address={branchEdit.originAddress} normalizedAddress={branchEdit.originNormalizedAddress} latitude={branchEdit.originLatitude} longitude={branchEdit.originLongitude} status={branchEdit.originConfirmationStatus} onConfirmed={(origin) => setBranchEdit({ ...branchEdit, originAddress: String(origin.origin_address ?? ""), originNormalizedAddress: String(origin.origin_normalized_address ?? ""), originLatitude: Number(origin.origin_latitude), originLongitude: Number(origin.origin_longitude), originConfirmationStatus: "CONFIRMED", shippingV2ConfigVersion: Number(origin.config_version ?? branchEdit.shippingV2ConfigVersion) })} /> : <Notice>Buat cabang terlebih dahulu sebelum mengonfirmasi origin pengiriman.</Notice>}
                <div className="two-field-grid">
                  <label>
                    Tarif kelebihan radius per km (Rp)
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={branchEdit.outsideRatePerKm ?? ""}
                      onChange={(event) =>
                        setBranchEdit({ ...branchEdit, outsideRatePerKm: event.target.value === "" ? null : Number(event.target.value) })
                      }
                    />
                  </label>
                  <label>
                    Jarak pengiriman maksimum (km)
                    <input
                      type="number"
                      min="0.1"
                      max="1000"
                      step="0.1"
                      value={branchEdit.maximumDeliveryDistance ?? ""}
                      onChange={(event) =>
                        setBranchEdit({ ...branchEdit, maximumDeliveryDistance: event.target.value === "" ? null : Number(event.target.value) })
                      }
                    />
                  </label>
                </div>
                <label>
                  Pembulatan jarak
                  <select
                    value={branchEdit.distanceRounding}
                    onChange={(event) => setBranchEdit({ ...branchEdit, distanceRounding: event.target.value as BranchRow["distanceRounding"] })}
                  >
                    <option value="CEIL">Bulatkan ke atas (CEIL)</option>
                    <option value="FLOOR">Bulatkan ke bawah (FLOOR)</option>
                  </select>
                </label>
                <label className="toggle-setting">
                  <input
                    type="checkbox"
                    checked={branchEdit.freeRadiusEnabled}
                    onChange={(event) => setBranchEdit({ ...branchEdit, freeRadiusEnabled: event.target.checked })}
                  />
                  <span><strong>Aktifkan radius gratis</strong><small>Berlaku dari titik asal cabang ke semua alamat pengiriman.</small></span>
                </label>
                {branchEdit.freeRadiusEnabled && (
                  <div className="two-field-grid">
                    <label>
                      Radius gratis (km)
                      <input type="number" min="0" max="1000" step="0.1" value={branchEdit.freeRadiusKm ?? ""} onChange={(event) => setBranchEdit({ ...branchEdit, freeRadiusKm: event.target.value === "" ? null : Number(event.target.value) })} />
                    </label>
                    <label>
                      Cakupan radius
                      <input value="Semua wilayah dari cabang" readOnly />
                      <span>Gratis jika syarat minimum belanja terpenuhi. Jika belum, ongkir dasar tetap berlaku; jarak di atas radius ditambahkan sesuai tarif per km.</span>
                    </label>
                  </div>
                )}
                <small>Versi konfigurasi: {branchEdit.shippingV2ConfigVersion}</small>
              </details>
            </fieldset>
            <div className="modal-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setBranchEdit(undefined);
                  setNewBranch(false);
                }}
              >
                Batal
              </Button>
              <Button type="submit" busy={busy} icon={<Save />}>
                {newBranch ? "Buat Cabang" : "Simpan Cabang"}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        open={Boolean(adminEdit)}
        onClose={() => {
          setAdminEdit(undefined);
          setPassword("");
        }}
        title={newAdmin ? "Tambah Admin" : "Ubah Admin & Akses"}
        description="Satu Admin hanya dapat aktif di satu cabang."
      >
        {adminEdit && (
          <form
            className="modal-form"
            onSubmit={(event) => void saveAdmin(event)}
          >
            <div className="access-form-heading">
              <strong>Akun Cabang Admin</strong>
              <small>
                Akun ini hanya dapat mengakses satu cabang yang dipilih Owner.
              </small>
            </div>
            <label>
              Nama Admin
              <input
                required
                minLength={3}
                value={adminEdit.name}
                onChange={(event) =>
                  setAdminEdit({ ...adminEdit, name: event.target.value })
                }
              />
            </label>
            <label>
              Username
              <input
                required
                minLength={3}
                autoCapitalize="none"
                value={adminEdit.username}
                onChange={(event) =>
                  setAdminEdit({
                    ...adminEdit,
                    username: event.target.value.toLowerCase(),
                  })
                }
              />
            </label>
            <label>
              Cabang
              <select
                required
                value={adminEdit.branchId}
                onChange={(event) =>
                  setAdminEdit({ ...adminEdit, branchId: event.target.value })
                }
              >
                <option value="">Pilih cabang</option>
                {branches
                  .filter((branch) => branch.status !== "inactive")
                  .map((branch) => (
                    <option value={branch.id} key={branch.id}>
                      {branch.name}
                    </option>
                  ))}
              </select>
              <span>
                Cabang dikunci dari sesi login dan tidak dapat diganti oleh Admin.
              </span>
            </label>
            {!newAdmin && (
              <label>
                Status
                <select
                  value={adminEdit.status}
                  onChange={(event) =>
                    setAdminEdit({ ...adminEdit, status: event.target.value })
                  }
                >
                  <option value="active">Aktif</option>
                  <option value="inactive">Nonaktif</option>
                </select>
              </label>
            )}
            <label>
              {newAdmin ? "Password" : "Password baru (opsional)"}
              <div className="password-field">
                <input
                  required={newAdmin}
                  minLength={12}
                  maxLength={128}
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  aria-label={
                    showPassword ? "Sembunyikan password" : "Tampilkan password"
                  }
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </button>
              </div>
              <span>
                Minimal 12 karakter. Hanya password baru yang bisa dilihat
                sebelum disimpan.
              </span>
            </label>
            <div className="modal-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setAdminEdit(undefined)}
              >
                Batal
              </Button>
              <Button type="submit" busy={busy}>
                {newAdmin ? "Buat Admin" : "Simpan Akses"}
              </Button>
            </div>
          </form>
        )}
      </Modal>
      <Modal
        open={Boolean(branchArchiveTarget)}
        onClose={() => setBranchArchiveTarget(undefined)}
        title="Hapus Cabang ke Arsip?"
        description={`${branchArchiveTarget?.name || "Cabang ini"} langsung hilang dari website utama dan dapat dipulihkan Owner selama 30 hari.`}
      >
        <Notice tone="warning">
          Stok, produk, laporan, dan histori pesanan tidak dihapus. Sesi Admin
          cabang ini langsung diputus agar tidak ada perubahan operasional.
        </Notice>
        <div className="modal-actions">
          <Button type="button" variant="secondary" onClick={() => setBranchArchiveTarget(undefined)}>Batal</Button>
          <Button type="button" variant="danger" busy={busy} onClick={() => void archiveBranch()} icon={<Trash2 />}>Hapus ke Arsip</Button>
        </div>
      </Modal>
      <Modal
        open={Boolean(adminArchiveTarget)}
        onClose={() => setAdminArchiveTarget(undefined)}
        title="Hapus Admin ke Arsip?"
        description={`${adminArchiveTarget?.name || "Admin ini"} langsung tidak dapat login dan dapat dipulihkan Owner selama 30 hari.`}
      >
        <Notice tone="warning">
          Setelah 30 hari identitas login dianonimkan dan akun tidak dapat
          dipulihkan lagi. Histori tindakan tetap aman untuk audit.
        </Notice>
        <div className="modal-actions">
          <Button type="button" variant="secondary" onClick={() => setAdminArchiveTarget(undefined)}>Batal</Button>
          <Button type="button" variant="danger" busy={busy} onClick={() => void archiveAdmin()} icon={<Trash2 />}>Hapus ke Arsip</Button>
        </div>
      </Modal>
      <Modal
        open={Boolean(temporaryPassword)}
        onClose={() => setTemporaryPassword("")}
        title="Password Baru Siap"
        description="Password hanya ditampilkan di layar ini. Salin sebelum menutup."
      >
        <div className="temporary-password">
          <code>{temporaryPassword}</code>
          <Button
            onClick={() =>
              void navigator.clipboard.writeText(temporaryPassword)
            }
          >
            Salin Password
          </Button>
          <Notice tone="warning">
            Setelah jendela ini ditutup, password tidak dapat ditampilkan lagi.
            Owner tetap bisa melakukan reset baru kapan saja.
          </Notice>
        </div>
      </Modal>
    </div>
  );
}

interface ProductBranch {
  branchId: string;
  branchName: string;
  price: number;
  promoPrice: number | null;
  stock: number;
  minimumStock: number;
  available: boolean;
  featured: boolean;
  mediaId?: string;
  branchMediaId?: string;
}

interface ProductOperation {
  id: string;
  name: string;
  unit: string;
  categoryName: string;
  globalMediaId?: string;
  branch: ProductBranch;
}

interface MediaOption {
  id: string;
  filename: string;
}

function mediaOptions(payload: unknown): MediaOption[] {
  return normalizeCollection(payload)
    .items.map((item) => ({
      id: asText(item.id),
      filename: asText(item.filename, "Foto produk"),
    }))
    .filter((item) => item.id);
}

export function CatalogOperationsPage({ role }: { role: SessionUser["role"] }) {
  const { user } = useSession();
  const prefix = role === "OWNER" ? "/api/owner" : "/api/admin";
  const [products, setProducts] = useState<ProductOperation[]>([]);
  const [media, setMedia] = useState<MediaOption[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busyId, setBusyId] = useState<string>();
  const [setValues, setSetValues] = useState<Record<string, string>>({});
  const [photoTarget, setPhotoTarget] = useState<ProductOperation>();
  const [selectedMediaId, setSelectedMediaId] = useState("");
  const [optimized, setOptimized] = useState<OptimizedImage>();

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const [productPayload, mediaPayload] = await Promise.all([
        apiRequest<unknown>(`${prefix}/products`),
        apiRequest<unknown>(`${prefix}/media`),
      ]);
      const rows: ProductOperation[] = [];
      for (const item of normalizeCollection(productPayload).items) {
        const productId = asText(item.id);
        const base = {
          id: productId,
          name: asText(item.name),
          unit: asText(item.unit, "pcs"),
          categoryName: asText(
            value(item, "categoryName", "category_name"),
            "Tanpa kategori",
          ),
          globalMediaId:
            asText(value(item, "globalMediaId", "global_media_id")) ||
            undefined,
        };
        if (role === "OWNER") {
          const branchItems = Array.isArray(item.branches)
            ? (item.branches as ApiRecord[])
            : [];
          for (const branch of branchItems) {
            rows.push({
              ...base,
              branch: {
                branchId: asText(value(branch, "branchId", "branch_id")),
                branchName: asText(value(branch, "branchName", "branch_name")),
                price: asNumber(branch.price),
                promoPrice:
                  branch.promoPrice === null || branch.promo_price === null
                    ? null
                    : asNumber(value(branch, "promoPrice", "promo_price")),
                stock: asNumber(branch.stock),
                minimumStock: asNumber(
                  value(branch, "minimumStock", "minimum_stock"),
                ),
                available: asBoolean(
                  value(branch, "available", "is_available"),
                ),
                featured: asBoolean(value(branch, "featured", "is_featured")),
                mediaId:
                  asText(value(branch, "mediaId", "media_id")) || undefined,
                branchMediaId:
                  asText(value(branch, "branchMediaId", "branch_media_id")) ||
                  undefined,
              },
            });
          }
        } else {
          rows.push({
            ...base,
            branch: {
              branchId: asText(user?.branchId),
              branchName: user?.branchName ?? "Cabang Anda",
              price: asNumber(item.price),
              promoPrice:
                item.promo_price === null
                  ? null
                  : asNumber(value(item, "promoPrice", "promo_price")),
              stock: asNumber(value(item, "stock", "quantity")),
              minimumStock: asNumber(
                value(item, "minimumStock", "minimum_stock"),
              ),
              available: asBoolean(value(item, "isAvailable", "is_available")),
              featured: asBoolean(value(item, "isFeatured", "is_featured")),
              mediaId: asText(value(item, "mediaId", "media_id")) || undefined,
              branchMediaId:
                asText(value(item, "branchMediaId", "branch_media_id")) ||
                undefined,
            },
          });
        }
      }
      setProducts(rows);
      setMedia(mediaOptions(mediaPayload));
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Katalog cabang belum dapat dimuat.",
      );
    } finally {
      setLoading(false);
    }
  }, [prefix, role, user]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);

  const keyFor = (row: ProductOperation) => `${row.id}:${row.branch.branchId}`;
  const adjust = async (
    row: ProductOperation,
    type: "ADD" | "SUBTRACT" | "SET",
    quantity: number,
  ) => {
    const key = keyFor(row);
    setBusyId(key);
    setNotice(undefined);
    try {
      const endpoint =
        role === "OWNER"
          ? `/api/owner/stock/${encodeURIComponent(row.branch.branchId)}/${encodeURIComponent(row.id)}/adjust`
          : `/api/admin/stock/${encodeURIComponent(row.id)}/adjust`;
      await apiRequest(endpoint, {
        method: "POST",
        ...jsonBody({
          type,
          quantity,
          note: "Penyesuaian cepat dari kartu produk",
        }),
      });
      setNotice(
        `Stok ${row.name} di ${row.branch.branchName} berhasil diperbarui.`,
      );
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Stok belum berhasil diperbarui.",
      );
    } finally {
      setBusyId(undefined);
    }
  };

  const updateAvailability = async (
    row: ProductOperation,
    available: boolean,
  ) => {
    const key = keyFor(row);
    setBusyId(key);
    setNotice(undefined);
    try {
      const endpoint =
        role === "OWNER"
          ? `/api/owner/products/${encodeURIComponent(row.id)}/branch/${encodeURIComponent(row.branch.branchId)}`
          : `/api/admin/products/${encodeURIComponent(row.id)}`;
      await apiRequest(endpoint, {
        method: "PATCH",
        ...jsonBody({
          price: row.branch.price,
          promoPrice: row.branch.promoPrice,
          isAvailable: available,
          isFeatured: row.branch.featured,
        }),
      });
      setNotice(
        `${row.name} sekarang ${available ? "tersedia" : "ditandai habis"}.`,
      );
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Ketersediaan belum berhasil diperbarui.",
      );
    } finally {
      setBusyId(undefined);
    }
  };

  const markOut = async (row: ProductOperation) => {
    await adjust(row, "SET", 0);
    await updateAvailability(row, false);
  };

  const attachMedia = async (mediaId: string) => {
    if (!photoTarget) return;
    const key = keyFor(photoTarget);
    setBusyId(key);
    setNotice(undefined);
    try {
      const endpoint = `${prefix}/products/${encodeURIComponent(photoTarget.id)}/media`;
      await apiRequest(endpoint, {
        method: "PATCH",
        ...jsonBody({
          mediaId: mediaId || null,
          ...(role === "OWNER"
            ? { branchId: photoTarget.branch.branchId }
            : {}),
        }),
      });
      setPhotoTarget(undefined);
      setOptimized(undefined);
      setSelectedMediaId("");
      setNotice(`Foto ${photoTarget.name} berhasil diperbarui.`);
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Foto belum berhasil dipasang.",
      );
    } finally {
      setBusyId(undefined);
    }
  };

  const chooseFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setOptimized(await optimizeProductImage(file));
    } catch (caught) {
      setNotice(
        caught instanceof Error ? caught.message : "Foto belum dapat diproses.",
      );
    }
  };

  const uploadAndAttach = async () => {
    if (!optimized || !photoTarget) return;
    const key = keyFor(photoTarget);
    setBusyId(key);
    try {
      const uploaded = await apiRequest<{ id: string }>("/api/media", {
        method: "POST",
        ...jsonBody({
          filename: optimized.filename,
          originalFilename: optimized.filename,
          mediaType: "product",
          altText: `Foto ${photoTarget.name}`,
          dataBase64: await blobToBase64(optimized.blob),
        }),
      });
      await attachMedia(uploaded.id);
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Foto belum berhasil diunggah.",
      );
      setBusyId(undefined);
    }
  };

  const visible = useMemo(
    () =>
      products.filter((row) =>
        `${row.name} ${row.categoryName} ${row.branch.branchName}`
          .toLowerCase()
          .includes(query.toLowerCase()),
      ),
    [products, query],
  );

  return (
    <div className="dashboard-page">
      <div className="dashboard-heading">
        <div>
          <span className="eyebrow">Katalog visual</span>
          <h1>Produk, Foto & Stok</h1>
          <p>
            Kelola stok langsung dari kartu produk. Gunakan foto yang sudah ada
            atau unggah foto baru yang otomatis dioptimalkan.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => void load()}
          icon={<RefreshCw />}
        >
          Perbarui
        </Button>
      </div>
      {role === "ADMIN" && (
        <Notice tone="info">
          Admin Cabang · {user?.branchName} 🔒 Foto global dapat digunakan
          ulang; foto cabang lain tetap terlindungi.
        </Notice>
      )}
      {notice && (
        <Notice
          tone={
            notice.includes("berhasil") || notice.includes("sekarang")
              ? "success"
              : "danger"
          }
        >
          {notice}
        </Notice>
      )}
      <div className="toolbar">
        <label className="dashboard-search">
          <span className="sr-only">Cari produk</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Cari produk, kategori, atau cabang…"
          />
        </label>
      </div>
      {loading && <Skeleton rows={8} cards />}
      {error && <ErrorState message={error} onRetry={() => void load()} />}
      {!loading && !error && visible.length === 0 && (
        <EmptyState title="Produk tidak ditemukan" />
      )}
      {!loading && !error && visible.length > 0 && (
        <div className="catalog-operation-grid">
          {visible.map((row) => {
            const key = keyFor(row);
            const out = row.branch.stock === 0 || !row.branch.available;
            return (
              <article
                className={`catalog-operation-card ${out ? "catalog-operation-card--out" : ""}`}
                key={key}
              >
                <div className="catalog-operation-card__image">
                  <MediaImage mediaId={row.branch.mediaId} alt={row.name} />
                  <button
                    type="button"
                    onClick={() => {
                      setPhotoTarget(row);
                      setSelectedMediaId(
                        row.branch.branchMediaId ?? row.branch.mediaId ?? "",
                      );
                    }}
                  >
                    <Camera />
                    <span>Ubah foto</span>
                  </button>
                </div>
                <div className="catalog-operation-card__body">
                  <div className="catalog-operation-card__meta">
                    <span>{row.categoryName}</span>
                    <span>{row.branch.branchName}</span>
                  </div>
                  <h2>{row.name}</h2>
                  <p>
                    {formatRupiah(row.branch.promoPrice ?? row.branch.price)} /{" "}
                    {row.unit}
                  </p>
                  <div className="stock-display">
                    <span>Stok saat ini</span>
                    <strong>{row.branch.stock}</strong>
                    <small>
                      {row.branch.stock <= row.branch.minimumStock
                        ? "Stok rendah"
                        : "Stok aman"}
                    </small>
                  </div>
                  <div className="quick-stock">
                    <Button
                      variant="secondary"
                      aria-label={`Kurangi satu ${row.name}`}
                      disabled={busyId === key || row.branch.stock === 0}
                      onClick={() => void adjust(row, "SUBTRACT", 1)}
                      icon={<Minus />}
                    >
                      1
                    </Button>
                    <Button
                      variant="secondary"
                      aria-label={`Tambah satu ${row.name}`}
                      disabled={busyId === key}
                      onClick={() => void adjust(row, "ADD", 1)}
                      icon={<Plus />}
                    >
                      1
                    </Button>
                    <Button
                      variant="danger"
                      disabled={busyId === key || out}
                      onClick={() => void markOut(row)}
                      icon={<PackageX />}
                    >
                      Habis
                    </Button>
                  </div>
                  <div className="set-stock">
                    <input
                      inputMode="numeric"
                      min="0"
                      type="number"
                      aria-label={`Total stok ${row.name}`}
                      placeholder="Total stok"
                      value={setValues[key] ?? ""}
                      onChange={(event) =>
                        setSetValues((current) => ({
                          ...current,
                          [key]: event.target.value,
                        }))
                      }
                    />
                    <Button
                      disabled={
                        busyId === key ||
                        setValues[key] === undefined ||
                        setValues[key] === ""
                      }
                      onClick={() =>
                        void adjust(
                          row,
                          "SET",
                          Math.max(0, Number(setValues[key])),
                        )
                      }
                    >
                      Set Total
                    </Button>
                  </div>
                  {out && (
                    <Button
                      className="button--full"
                      disabled={busyId === key}
                      onClick={() => void updateAvailability(row, true)}
                      icon={<CheckCircle2 />}
                    >
                      Aktifkan Kembali
                    </Button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Modal
        open={Boolean(photoTarget)}
        onClose={() => {
          setPhotoTarget(undefined);
          setOptimized(undefined);
        }}
        title={`Foto ${photoTarget?.name ?? "Produk"}`}
        description={`Foto ini khusus tampilan ${photoTarget?.branch.branchName ?? "cabang"}.`}
      >
        {photoTarget && (
          <div className="photo-picker">
            <section>
              <h3>Gunakan gambar yang sudah ada</h3>
              <select
                value={selectedMediaId}
                onChange={(event) => setSelectedMediaId(event.target.value)}
              >
                <option value="">Pakai foto global / tanpa override</option>
                {media.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.filename} · {item.id.slice(0, 8)}
                  </option>
                ))}
              </select>
              {selectedMediaId && (
                <img
                  src={`/api/media/private/${encodeURIComponent(selectedMediaId)}`}
                  alt="Pratinjau foto terpilih"
                />
              )}
              <Button
                className="button--full"
                disabled={busyId === keyFor(photoTarget)}
                onClick={() => void attachMedia(selectedMediaId)}
                icon={<ImagePlus />}
              >
                Gunakan Foto Ini
              </Button>
            </section>
            <div className="photo-picker__or">atau</div>
            <section>
              <h3>Unggah foto baru</h3>
              <label className="compact-upload">
                <ImagePlus />
                <span>Pilih JPG, PNG, atau WebP</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(event) => void chooseFile(event)}
                />
              </label>
              {optimized && (
                <div className="optimized-inline">
                  <img
                    src={URL.createObjectURL(optimized.blob)}
                    alt="Pratinjau hasil optimasi"
                  />
                  <p>
                    {Math.round(optimized.optimizedBytes / 1024)} KB · WebP
                    800×800 · hemat {optimized.savingPercent}%
                  </p>
                  <Button
                    className="button--full"
                    busy={busyId === keyFor(photoTarget)}
                    onClick={() => void uploadAndAttach()}
                    icon={<Send />}
                  >
                    Unggah & Pakai
                  </Button>
                </div>
              )}
            </section>
          </div>
        )}
      </Modal>
    </div>
  );
}

function orderNext(status: string, deliveryType: string): string | undefined {
  if (status === "NEW" || status === "WAITING_CONFIRMATION") return "CONFIRMED";
  if (status === "CONFIRMED" || status === "PAID") return "PREPARING";
  if (status === "PREPARING")
    return deliveryType === "PICKUP"
      ? "READY_FOR_PICKUP"
      : "READY_FOR_DELIVERY";
  if (status === "READY_FOR_DELIVERY") return "DELIVERING";
  if (status === "READY_FOR_PICKUP") return "COMPLETED";
  return undefined;
}

export function OrderOperationsPage({
  role,
  paymentsOnly = false,
}: {
  role: SessionUser["role"];
  paymentsOnly?: boolean;
}) {
  const endpoint = role === "OWNER" ? "/api/owner/orders" : "/api/admin/orders";
  const [items, setItems] = useState<ApiRecord[]>([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState(
    paymentsOnly ? "WAITING_VERIFICATION" : "",
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busyId, setBusyId] = useState<string>();
  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const params = new URLSearchParams();
      if (query) params.set("q", query);
      if (status) params.set("status", status);
      params.set("limit", "50");
      setItems(
        normalizeCollection(
          await apiRequest<unknown>(`${endpoint}?${params.toString()}`),
        ).items,
      );
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Pesanan belum dapat dimuat.",
      );
    } finally {
      setLoading(false);
    }
  }, [endpoint, query, status]);
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), query ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [load, query]);
  const update = async (
    item: ApiRecord,
    nextStatus: string,
    payment = false,
  ) => {
    const id = asText(item.id);
    setBusyId(id);
    setNotice(undefined);
    try {
      await apiRequest(
        `${endpoint}/${encodeURIComponent(id)}/${payment ? "payment" : "status"}`,
        {
          method: "PATCH",
          ...jsonBody(
            payment
              ? { status: nextStatus }
              : {
                  status: nextStatus,
                  ...(["CANCELLED", "REJECTED"].includes(nextStatus)
                    ? { reason: "Diperbarui melalui dashboard" }
                    : {}),
                },
          ),
        },
      );
      setNotice("Status pesanan berhasil diperbarui.");
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Status belum berhasil diperbarui.",
      );
    } finally {
      setBusyId(undefined);
    }
  };
  return (
    <div className="dashboard-page">
      <div className="dashboard-heading">
        <div>
          <span className="eyebrow">Operasional cabang</span>
          <h1>{paymentsOnly ? "Pembayaran" : "Pesanan"}</h1>
          <p>
            {paymentsOnly
              ? "Verifikasi pembayaran manual dengan jelas."
              : "Cabang, pelanggan, WhatsApp, pengiriman, dan notifikasi terlihat dalam satu kartu."}
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => void load()}
          icon={<RefreshCw />}
        >
          Perbarui
        </Button>
      </div>
      {!paymentsOnly && (
        <Notice tone="info">
          Setelah pesanan antar diserahkan ke pengirim, reply pesan WhatsApp
          yang terkait dengan tulisan <strong>SELESAI</strong>. Sistem menutup
          pesanan otomatis saat webhook Cloud API aktif.
        </Notice>
      )}
      {notice && (
        <Notice tone={notice.includes("berhasil") ? "success" : "danger"}>
          {notice}
        </Notice>
      )}
      <div className="toolbar">
        <label className="dashboard-search">
          <Search />
          <span className="sr-only">Cari pesanan</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Nomor pesanan atau pelanggan…"
          />
        </label>
        <label className="toolbar-filter">
          <Filter />
          <span className="sr-only">Filter status</span>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">Semua status</option>
            <option value="NEW">Pesanan baru</option>
            <option value="CONFIRMED">Dikonfirmasi</option>
            <option value="PREPARING">Disiapkan</option>
            <option value="READY_FOR_DELIVERY">Siap dikirim</option>
            <option value="DELIVERING">Sedang dikirim</option>
            <option value="READY_FOR_PICKUP">Siap diambil</option>
            <option value="WAITING_VERIFICATION">Verifikasi pembayaran</option>
            <option value="COMPLETED">Selesai</option>
            <option value="CANCELLED">Dibatalkan</option>
          </select>
        </label>
      </div>
      {loading && <Skeleton rows={6} />}
      {error && <ErrorState message={error} onRetry={() => void load()} />}
      {!loading && !error && items.length === 0 && (
        <EmptyState
          title={
            paymentsOnly
              ? "Belum ada pembayaran yang perlu ditinjau"
              : "Belum ada pesanan"
          }
        />
      )}
      {!loading && !error && items.length > 0 && (
        <div className="order-management-list">
          {items.map((item) => {
            const id = asText(item.id);
            const orderStatus = asText(
              value(item, "orderStatus", "order_status"),
            );
            const paymentStatus = asText(
              value(item, "paymentStatus", "payment_status"),
            );
            const deliveryType = asText(
              value(item, "deliveryType", "delivery_type"),
              "PICKUP",
            );
            const next = orderNext(orderStatus, deliveryType);
            const branchName = asText(
              value(item, "branchName", "branch_name", "branch_name_snapshot"),
              "Cabang",
            );
            return (
              <article
                className={`management-card management-card--${orderStatus.toLowerCase()}`}
                key={id}
              >
                <div className="management-card__top">
                  <div>
                    <span className="order-branch-label">{branchName}</span>
                    <strong>
                      {asText(value(item, "orderNumber", "order_number"))}
                    </strong>
                  </div>
                  <StatusBadge
                    status={paymentsOnly ? paymentStatus : orderStatus}
                  />
                </div>
                <div className="management-card__facts management-card__facts--six">
                  <span>
                    <small>Pelanggan</small>
                    <strong>
                      {asText(
                        value(
                          item,
                          "customerName",
                          "customer_name",
                          "customer_name_snapshot",
                        ),
                      )}
                    </strong>
                  </span>
                  <span>
                    <small>WhatsApp</small>
                    <strong>
                      {asText(
                        value(
                          item,
                          "whatsappNumber",
                          "whatsapp_number",
                          "customer_whatsapp_snapshot",
                        ),
                      )}
                    </strong>
                  </span>
                  <span>
                    <small>Total</small>
                    <strong>{formatRupiah(asNumber(item.total))}</strong>
                  </span>
                  <span>
                    <small>Pengiriman</small>
                    <strong>
                      {deliveryType === "DELIVERY"
                        ? "Antar ke pelanggan"
                        : "Ambil di toko"}
                    </strong>
                  </span>
                  <span>
                    <small>Notifikasi WA</small>
                    <strong>
                      {asText(
                        value(item, "whatsappStatuses", "whatsapp_statuses"),
                        "Belum dikirim",
                      )}
                    </strong>
                  </span>
                  <span>
                    <small>Waktu</small>
                    <strong>
                      {formatDateTime(
                        asText(value(item, "createdAt", "created_at")),
                      )}
                    </strong>
                  </span>
                </div>
                {orderStatus === "DELIVERING" && (
                  <Notice tone="warning">
                    Sudah diserahkan? Reply pesan WhatsApp ini dengan{" "}
                    <strong>SELESAI</strong>. Selesai Manual tersedia sebagai
                    cadangan.
                  </Notice>
                )}
                <div className="management-card__actions">
                  {paymentsOnly ? (
                    <>
                      <Button
                        busy={busyId === id}
                        onClick={() => void update(item, "PAID", true)}
                        icon={<CheckCircle2 />}
                      >
                        Tandai Dibayar
                      </Button>
                      <Button
                        variant="danger"
                        disabled={busyId === id}
                        onClick={() => void update(item, "REJECTED", true)}
                      >
                        Tolak
                      </Button>
                    </>
                  ) : (
                    <>
                      {next && (
                        <Button
                          busy={busyId === id}
                          onClick={() => void update(item, next)}
                        >
                          Lanjut: {statusLabel(next)}
                        </Button>
                      )}
                      {orderStatus === "DELIVERING" && (
                        <Button
                          variant="secondary"
                          busy={busyId === id}
                          onClick={() => void update(item, "COMPLETED")}
                        >
                          Selesai Manual
                        </Button>
                      )}
                      {!["COMPLETED", "CANCELLED", "REJECTED"].includes(
                        orderStatus,
                      ) && (
                        <Button
                          variant="danger"
                          disabled={busyId === id}
                          onClick={() => void update(item, "CANCELLED")}
                        >
                          Batalkan
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface WhatsAppData extends ApiRecord {
  cloudApiConfigured?: boolean;
  webhookSignatureConfigured?: boolean;
  webhookVerificationConfigured?: boolean;
  ownerNumber?: string | null;
  orderTemplate?: string | null;
  completedTemplate?: string | null;
  languageCode?: string;
  webhookUrl?: string;
  branches?: ApiRecord[];
  outbox?: ApiRecord[];
}

export function WhatsAppSettingsPage() {
  const [data, setData] = useState<WhatsAppData>();
  const [form, setForm] = useState({
    ownerNumber: "",
    orderTemplate: "",
    completedTemplate: "",
    languageCode: "id",
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string>();
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiRequest<WhatsAppData>("/api/owner/whatsapp");
      setData(result);
      setForm({
        ownerNumber: result.ownerNumber ?? "",
        orderTemplate: result.orderTemplate ?? "",
        completedTemplate: result.completedTemplate ?? "",
        languageCode: result.languageCode ?? "id",
      });
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Pengaturan WhatsApp belum dapat dimuat.",
      );
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  const save = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setNotice(undefined);
    try {
      await apiRequest("/api/owner/whatsapp", {
        method: "PATCH",
        ...jsonBody({
          ownerNumber: form.ownerNumber || null,
          orderTemplate: form.orderTemplate || null,
          completedTemplate: form.completedTemplate || null,
          languageCode: form.languageCode,
        }),
      });
      setNotice("Pengaturan WhatsApp berhasil disimpan.");
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Pengaturan belum berhasil disimpan.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="dashboard-page">
      <div className="dashboard-heading">
        <div>
          <span className="eyebrow">Integrasi komunikasi</span>
          <h1>WhatsApp Website & Pesanan</h1>
          <p>
            Tombol chat memakai nomor cabang aktif. Cloud API mengirim pesanan
            ke Admin cabang dan Owner serta menerima balasan SELESAI.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => void load()}
          icon={<RefreshCw />}
        >
          Perbarui
        </Button>
      </div>
      {notice && (
        <Notice tone={notice.includes("berhasil") ? "success" : "danger"}>
          {notice}
        </Notice>
      )}
      {loading ? (
        <Skeleton rows={5} />
      ) : (
        <>
          <div className="connection-grid">
            <article>
              <span>Pengiriman Cloud API</span>
              <strong>
                {data?.cloudApiConfigured ? "Terhubung" : "Belum aktif"}
              </strong>
              <StatusBadge
                status={data?.cloudApiConfigured ? "active" : "inactive"}
              />
            </article>
            <article>
              <span>Verifikasi webhook</span>
              <strong>
                {data?.webhookVerificationConfigured &&
                data.webhookSignatureConfigured
                  ? "Siap"
                  : "Belum lengkap"}
              </strong>
              <StatusBadge
                status={
                  data?.webhookVerificationConfigured &&
                  data.webhookSignatureConfigured
                    ? "active"
                    : "inactive"
                }
              />
            </article>
            <article>
              <span>Cabang dengan nomor chat</span>
              <strong>
                {
                  (data?.branches ?? []).filter((branch) =>
                    asText(branch.whatsapp_number),
                  ).length
                }
              </strong>
              <StatusBadge status="active" />
            </article>
          </div>
          {!data?.cloudApiConfigured && (
            <Notice tone="warning">
              Chat langsung wa.me akan berfungsi setelah nomor cabang asli
              disimpan. Pengiriman otomatis dan balasan SELESAI memerlukan
              secret Meta WhatsApp Business Cloud API di Cloudflare.
            </Notice>
          )}
          <section className="operations-section">
            <h2>Nomor & template</h2>
            <form
              className="whatsapp-form"
              onSubmit={(event) => void save(event)}
            >
              <label>
                Nomor WhatsApp Owner
                <input
                  inputMode="tel"
                  placeholder="Kode negara + nomor WhatsApp Owner"
                  value={form.ownerNumber}
                  onChange={(event) =>
                    setForm({ ...form, ownerNumber: event.target.value })
                  }
                />
                <span>
                  Bisa nomor negara mana saja. Owner menerima notifikasi dari
                  semua cabang.
                </span>
              </label>
              <label>
                Template pesanan baru
                <input
                  placeholder="Nama template Meta yang disetujui"
                  value={form.orderTemplate}
                  onChange={(event) =>
                    setForm({ ...form, orderTemplate: event.target.value })
                  }
                />
              </label>
              <label>
                Template pesanan selesai
                <input
                  placeholder="Opsional"
                  value={form.completedTemplate}
                  onChange={(event) =>
                    setForm({ ...form, completedTemplate: event.target.value })
                  }
                />
              </label>
              <label>
                Kode bahasa
                <input
                  value={form.languageCode}
                  onChange={(event) =>
                    setForm({ ...form, languageCode: event.target.value })
                  }
                />
              </label>
              <label>
                URL webhook
                <input readOnly value={data?.webhookUrl ?? ""} />
              </label>
              <Button type="submit" busy={busy} icon={<Save />}>
                Simpan WhatsApp
              </Button>
            </form>
          </section>
          <section className="operations-section">
            <div className="panel-heading">
              <div>
                <h2>Nomor chat per cabang</h2>
                <p>Ubah nomor cabang pada halaman Admin & Akses.</p>
              </div>
              <Link className="button button--secondary" to="/owner/admin">
                Atur Cabang
              </Link>
            </div>
            <div className="simple-list">
              {(data?.branches ?? []).map((branch) => (
                <div key={asText(branch.id)}>
                  <span>
                    <strong>{asText(branch.name)}</strong>
                    <small>
                      {asText(branch.whatsapp_number) || "Belum diisi"}
                    </small>
                  </span>
                  {asText(branch.whatsapp_number) && (
                    <a
                      href={`https://wa.me/${asText(branch.whatsapp_number).replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <MessageCircle /> Tes Chat
                    </a>
                  )}
                </div>
              ))}
            </div>
          </section>
          <section className="operations-section">
            <h2>Pengiriman terbaru</h2>
            <div className="simple-list">
              {(data?.outbox ?? []).slice(0, 20).map((item) => (
                <div key={asText(item.id)}>
                  <span>
                    <strong>
                      {asText(item.order_number)} · {asText(item.branch_name)}
                    </strong>
                    <small>
                      {asText(item.recipient_type)} ·{" "}
                      {asText(item.recipient_number)}
                    </small>
                  </span>
                  <StatusBadge status={asText(item.status)} />
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
